// client/src/components/common/PickerCalendar.tsx
//
// The pickers' calendar (ST-00204, ST-00294): one month in the phone sheet, sized for a thumb
// (≥ 44 px day cells, 44 px month arrows and a Today button), or — `months={2}` — two months
// side by side in the desktop range popup (36 px days there, PickerPopover.css), the previous
// arrow on the left month and the next arrow on the right one, both moving the view by a month.
//
// An ARIA grid per month (WAI-ARIA APG date-picker dialog) with ONE tab stop across all the
// months shown: arrows move by day / week, Home / End to the week's ends, PageUp / PageDown by
// month (with Shift by year), Enter / Space pick; moving past the last month shown (or before
// the first) turns the view so the day stays in sight. Days outside the bounds stay focusable
// but cannot be picked (aria-disabled), so the keyboard never gets stuck.

import { useEffect, useId, useRef, useState, type KeyboardEvent, type Ref } from 'react';
import {
  WEEKDAYS,
  addDays,
  addMonths,
  blockedAbove,
  blockedBelow,
  clampIntoMonth,
  dayKey,
  endOfMonth,
  formatDayLong,
  formatMonthYear,
  isDayDisabled,
  monthMatrix,
  monthShown,
  sameDay,
  startOfDay,
  startOfMonth,
  type PickerBounds,
} from './pickerLogic';

export interface PickerCalendarProps {
  /** A day in the (first) month on show. */
  month: Date;
  onMonthChange: (month: Date) => void;
  /** How many months to show side by side (the desktop range popup shows 2). */
  months?: 1 | 2;
  /** Single selection (a day, a date-time's date). */
  selected?: Date | null;
  /** Range selection: both ends are marked and the days between are banded. */
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  bounds?: PickerBounds;
  min?: Date | null;
  max?: Date | null;
  onSelect: (day: Date) => void;
  /** Injected in tests; defaults to now. */
  today?: Date;
  /** Receives the day button that holds the tab stop (the sheet focuses it on open). */
  activeDayRef?: Ref<HTMLButtonElement>;
  /** The Today button (off where a "Today" preset already does the job). */
  showToday?: boolean;
}

const ArrowIcon = ({ d }: { d: string }) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const PREV = 'M12.5 4.5L7 10l5.5 5.5';
const NEXT = 'M7.5 4.5L13 10l-5.5 5.5';

export default function PickerCalendar({
  month,
  onMonthChange,
  months = 1,
  selected = null,
  rangeStart = null,
  rangeEnd = null,
  bounds = 'all',
  min = null,
  max = null,
  onSelect,
  today: todayProp,
  activeDayRef,
  showToday = true,
}: PickerCalendarProps) {
  const today = startOfDay(todayProp ?? new Date());
  const limits = { bounds, min, max, today };
  const baseId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const moved = useRef(false);
  const first = startOfMonth(month);
  const shown = (d: Date) => monthShown(d, first, months);

  const initial = selected ?? rangeStart ?? today;
  const [active, setActive] = useState<Date>(() =>
    shown(initial) ? startOfDay(initial) : clampIntoMonth(initial, month)
  );

  // The months changed from outside (arrows, a preset, a typed date, the other end's tab): keep
  // the tab stop on the selection when it is on show, else on the same day of the first month.
  const monthKey = `${month.getFullYear()}-${month.getMonth()}-${months}`;
  useEffect(() => {
    setActive((cur) => {
      if (shown(cur)) return cur;
      const anchor = [selected, rangeEnd, rangeStart].find((d) => d && shown(d));
      return anchor ? startOfDay(anchor) : clampIntoMonth(cur, month);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey]);

  // After keyboard navigation, focus follows the tab stop.
  useEffect(() => {
    if (!moved.current) return;
    moved.current = false;
    rootRef.current?.querySelector<HTMLButtonElement>('.picker-cal__day[tabindex="0"]')?.focus();
  }, [active]);

  const moveTo = (day: Date) => {
    moved.current = true;
    setActive(day);
    if (shown(day)) return;
    // Past the months on show: turn the view so the day is in the first (going back) or the
    // last (going forward) month.
    onMonthChange(dayKey(day) < dayKey(first) ? startOfMonth(day) : addMonths(day, 1 - months));
  };

  const pick = (day: Date) => {
    setActive(day);
    if (!isDayDisabled(day, limits)) onSelect(day);
  };

  const onGridKey = (e: KeyboardEvent<HTMLTableElement>) => {
    const byMonth = (n: number) => clampIntoMonth(active, addMonths(active, n));
    const next: Record<string, () => Date> = {
      ArrowLeft: () => addDays(active, -1),
      ArrowRight: () => addDays(active, 1),
      ArrowUp: () => addDays(active, -7),
      ArrowDown: () => addDays(active, 7),
      Home: () => addDays(active, -active.getDay()),
      End: () => addDays(active, 6 - active.getDay()),
      PageUp: () => byMonth(e.shiftKey ? -12 : -1),
      PageDown: () => byMonth(e.shiftKey ? 12 : 1),
    };
    if (next[e.key]) {
      e.preventDefault();
      moveTo(startOfDay(next[e.key]!()));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(active);
    }
  };

  const prevMonth = addMonths(first, -1);
  const nextMonth = addMonths(first, 1);
  const prevBlocked = blockedBelow(endOfMonth(prevMonth), limits);
  const nextBlocked = blockedAbove(addMonths(first, months), limits);
  const todayBlocked = isDayDisabled(today, limits);

  const lo = rangeStart && rangeEnd ? Math.min(dayKey(rangeStart), dayKey(rangeEnd)) : null;
  const hi = rangeStart && rangeEnd ? Math.max(dayKey(rangeStart), dayKey(rangeEnd)) : null;
  const isRange = lo !== null && hi !== null && lo !== hi;

  const prevButton = (
    <button
      type="button"
      className="picker-cal__nav"
      data-picker-nav
      onClick={() => onMonthChange(prevMonth)}
      disabled={prevBlocked}
      aria-label="Previous month"
    >
      <ArrowIcon d={PREV} />
    </button>
  );
  const nextButton = (
    <button
      type="button"
      className="picker-cal__nav"
      data-picker-nav
      onClick={() => onMonthChange(nextMonth)}
      disabled={nextBlocked}
      aria-label="Next month"
    >
      <ArrowIcon d={NEXT} />
    </button>
  );
  const todayButton = showToday && (
    <button
      type="button"
      className="picker-cal__today"
      onClick={() => {
        onMonthChange(startOfMonth(today));
        pick(today);
      }}
      aria-disabled={todayBlocked || undefined}
      title={todayBlocked ? 'Today is outside the dates you can choose here' : undefined}
    >
      Today
    </button>
  );

  const grid = (shownMonth: Date, headingId: string) => (
    <table role="grid" aria-labelledby={headingId} className="picker-cal__grid" onKeyDown={onGridKey}>
      <thead>
        <tr>
          {WEEKDAYS.map((w) => (
            <th key={w.short} scope="col" abbr={w.long}>
              {w.short}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {monthMatrix(shownMonth).map((week, wi) => (
          <tr key={wi}>
            {week.map((day, di) => {
              if (!day) return <td key={di} role="gridcell" />;
              const key = dayKey(day);
              const disabled = isDayDisabled(day, limits);
              const chosen = sameDay(day, selected) || (lo !== null && (key === lo || key === hi));
              const band = isRange && key > lo! && key < hi!;
              const isActive = sameDay(day, active);
              const cellClass = [
                band ? 'in-range' : '',
                isRange && key === lo ? 'range-start' : '',
                isRange && key === hi ? 'range-end' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <td key={di} role="gridcell" aria-selected={chosen} className={cellClass || undefined}>
                  <button
                    type="button"
                    ref={isActive ? activeDayRef : undefined}
                    tabIndex={isActive ? 0 : -1}
                    className={[
                      'picker-cal__day',
                      chosen ? 'is-selected' : '',
                      sameDay(day, today) ? 'is-today' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-label={formatDayLong(day)}
                    aria-current={sameDay(day, today) ? 'date' : undefined}
                    aria-disabled={disabled || undefined}
                    onClick={() => pick(day)}
                  >
                    {day.getDate()}
                  </button>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (months === 1) {
    const headingId = `${baseId}-0`;
    return (
      <div ref={rootRef} className="picker-cal">
        <div className="picker-cal__head">
          <h3 id={headingId} className="picker-cal__month" aria-live="polite">
            {formatMonthYear(first)}
          </h3>
          {todayButton}
          {prevButton}
          {nextButton}
        </div>
        {grid(first, headingId)}
      </div>
    );
  }

  const shownMonths = Array.from({ length: months }, (_, i) => addMonths(first, i));
  return (
    <div ref={rootRef} className="picker-cal picker-cal--multi">
      {shownMonths.map((m, i) => {
        const headingId = `${baseId}-${i}`;
        const isFirst = i === 0;
        const isLast = i === shownMonths.length - 1;
        return (
          <div key={`${m.getFullYear()}-${m.getMonth()}`} className="picker-cal__pane">
            <div className="picker-cal__head">
              {isFirst ? prevButton : <span className="picker-cal__nav-spacer" aria-hidden="true" />}
              <h3 id={headingId} className="picker-cal__month" aria-live="polite">
                {formatMonthYear(m)}
              </h3>
              {isLast && todayButton}
              {isLast ? nextButton : <span className="picker-cal__nav-spacer" aria-hidden="true" />}
            </div>
            {grid(m, headingId)}
          </div>
        );
      })}
    </div>
  );
}
